import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import { withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import MenuIcon from '@material-ui/icons/Menu';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';

import AppBarButton from '../../common-components/AppBarButton';
import AppBrand from '../../common-components/AppBrand';
import ThemeColors from '../../common-components/ThemeColors';
import { renderApp } from '../../index';

import i18n from '../../helpers/i18n';
import { withTranslation } from 'react-i18next';

import * as mainAppBarReducers from '../reducer';
import * as systemReducers from '../../system/reducer';
import * as appSideBarActions from '../../nav-bar/action-creator';
import * as systemAction from '../../system/action-creator';

const drawerWidth = 280;
const styles = theme => ({
  appBar: {
    marginLeft: drawerWidth,
    background: ThemeColors.appBar,
    zIndex: 1201,
    display: "flex",
    flexFlow: "row"
  },
  navIconHide: {
    marginLeft: -14,
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  toolbar: { flex: 1 },
  notiIcon: { fill: "#343A40" },
  badge: {
    color: "#343A40"
  },
  pageTitle: {
    flex: 1,
    color: "#343A40",
    fontWeight: 400
  },
  navIconStyle:{
    color: "#343A40",
    '&:hover': {
      transform: "scale(1.1)",
      transition: "all .3s ease-in-out",
    }
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.unit
  }
});

class MainAppBar extends Component {
  constructor() {
    super();
    this.state = {
      anchorEl: null,
    }

    this.handleMenu = this.handleMenu.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
  }

  handleMenu = event => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  handleLogout() {
    const { logout, userIdCheckingIntervalId } = this.props;
    this.setState({ anchorEl: null });
    clearInterval(userIdCheckingIntervalId);
    logout();
    console.log("logout")
    renderApp();
  }

  componentDidMount() {
    // Listen for language changes to update page title
    this.i18nLanguageChangedHandler = () => {
      this.updatePageTitle();
    };
    i18n.on('languageChanged', this.i18nLanguageChangedHandler);
    
    // Initial title update
    this.updatePageTitle();
  }

  componentWillUnmount() {
    // Remove language change listener
    if (this.i18nLanguageChangedHandler) {
      i18n.off('languageChanged', this.i18nLanguageChangedHandler);
    }
  }

  componentDidUpdate(prevProps) {
    // Update title if it changed from Redux
    if (prevProps.title !== this.props.title) {
      this.updatePageTitle();
    }
  }

  updatePageTitle = () => {
    const { title, t } = this.props;
    const pageTitleElement = document.getElementById("pageTitle");
    
    if (pageTitleElement) {
      if (title) {
        // Map common string values to translation keys
        const titleToKeyMap = {
          "Dashboard": "dashboard",
          "Users": "users",
          "Stations": "stations",
          "Historical Records": "historical-records",
          "Station Realtime Data": "station-realtime-data",
          "Station Statistics": "station-statistics",
          "Forecasting": "forecasting",
          "Fault Overview": "system-fault",
          "Stations Health": "station-health",
          "Download": "download",
          "Error Reports": "errors-reports",
          "New user": "new-user",
          "Edit user": "edit-user",
          "New station": "new-station",
          "Edit station": "edit-station"
        };
        
        // Try to get translation key from map, or use title as-is
        const translationKey = titleToKeyMap[title] || title.toLowerCase().replace(/\s+/g, '-');
        
        // Try to translate, fallback to original title if translation doesn't exist
        const translatedTitle = t(translationKey, { defaultValue: title });
        pageTitleElement.innerHTML = translatedTitle;
      }
      // If no title from Redux, keep whatever is set by Page component
    }
  }

  handleLanguageChange(langCode) {
    console.log('Changing language to:', langCode);
    // Change language using i18n
    i18n.changeLanguage(langCode).then(() => {
      // Save language preference to localStorage
      localStorage.setItem('i18nextLng', langCode);
      console.log('Language changed to:', langCode, 'Current language:', i18n.language);
      // Force re-render to update all translated components
      this.forceUpdate();
      // Also trigger a global update event
      window.dispatchEvent(new Event('languageChanged'));
    }).catch(err => {
      console.error('Error changing language:', err);
    });
  }

  render() {
    const { classes, /*leftElement, rightElement,*/ title, /*centerElement,*/ toggleMainSideBar, visible, t } = this.props;
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);

    if (!visible)
      return <div />;
    return (
      <AppBar className={classes.appBar}>
        <Hidden mdDown>
          <AppBrand />
        </Hidden>
        <Toolbar classes={{ root: classes.toolbar }}>
          <AppBarButton
            aria-label="open drawer"
            onClick={toggleMainSideBar}
            className={classes.navIconHide}
            Icon={MenuIcon}
          />
          <Typography variant="h6" classes={{ h6: classes.pageTitle }} noWrap><span id="pageTitle"></span></Typography>
          <div className={classes.actionsContainer}>
            <div className={classes.navIconStyle}>
              <Tooltip title="Account menu">
                <IconButton
                  aria-owns={open ? 'menu-appbar' : null}
                  aria-haspopup="true"
                  onClick={this.handleMenu}
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={this.handleClose}
              >
                <Tooltip title="Log out" placement="left" enterDelay={300}>
                  <MenuItem onClick={this.handleLogout}>Log out</MenuItem>
                </Tooltip>
              </Menu>
            </div>
          </div>
        </Toolbar>
      </AppBar>
    );
  }
}

MainAppBar.propTypes = {
  leftElement: PropTypes.node,
  rightElement: PropTypes.node,
  title: PropTypes.string,
  centerElement: PropTypes.node
}

const mapStateToProps = state => ({
  leftElement: mainAppBarReducers.getAppBarLeftElement(state),
  rightElement: mainAppBarReducers.getAppBarRightElement(state),
  centerElement: mainAppBarReducers.getAppBarCenterElement(state),
  title: mainAppBarReducers.getAppBarTitle(state),
  visible: mainAppBarReducers.isAppBarVisible(state),

  userIdCheckingIntervalId: systemReducers.getUserIdCheckingIntervalId(state)
});

export default connect(mapStateToProps, {
  toggleMainSideBar: appSideBarActions.toggleMainSideBar,
  logout: systemAction.logout
})(withTranslation()(withStyles(styles)(
  withRouter(MainAppBar)
)));
